import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import "../styles/EinblickePage.css";
import { getEinblicke, getEinblickeCategories } from "../lib/api";
import BlocksRenderer from "../components/BlocksRenderer";
import { Page, PageHeader, FilterChips } from "../components/ui";

const POSTS_PER_PAGE = 6;

export default function EinblickePage() {
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("Alle");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    Promise.all([getEinblicke(), getEinblickeCategories()])
      .then(([posts, cats]) => {
        setEntries(posts);
        setCategories(cats);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError(err);
        setLoading(false);
      });
  }, []);

  // Derive open post from URL param
  const openPostId = searchParams.get("post");
  const openPost = entries.find((e) => String(e.id) === openPostId || e.documentId === openPostId) || null;

  const openPostById = (entry) => {
    setSearchParams({ post: entry.documentId ?? entry.id });
  };

  const closePost = () => {
    setSearchParams({});
  };

  const filtered =
    activeCategory === "Alle"
      ? entries
      : entries.filter((e) => e.category === activeCategory);

  // Most recent = featured (first after sorting by date desc)
  const featured = filtered[0] || null;
  const rest = filtered.slice(1);

  const totalPages = Math.max(1, Math.ceil(rest.length / POSTS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const paginated = rest.slice((safePage - 1) * POSTS_PER_PAGE, safePage * POSTS_PER_PAGE);

  return (
    <Page className="ein-page" loading={loading} error={error}>
      <PageHeader
        parent={{ label: "Aktuelles", to: "/" }}
        current="Einblicke"
        title="Einblicke"
      />

      <section className="ein-section">
        <div className="ein-container">
          <FilterChips
            options={categories}
            value={activeCategory}
            allLabel="Alle"
            label="Kategorien"
            onChange={(val) => { setActiveCategory(val); setCurrentPage(1); }}
          />

          {loading ? <p>Lädt…</p> : null}

          {!loading && featured ? (
            <article className="ein-featured" onClick={() => openPostById(featured)}>
              <div className="ein-featured__media">
                <img src={featured.image} alt={featured.title} />
              </div>
              <div className="ein-featured__body">
                <div className="ein-meta">
                  {featured.category ? <span className="ein-pill">{featured.category}</span> : null}
                  {featured.date ? <span className="ein-date">{featured.date}</span> : null}
                </div>
                <h2 className="ein-featured__title">{featured.title}</h2>
                <p className="ein-featured__excerpt">{featured.excerpt}</p>
                <button
                  className="ein-link ein-link--button"
                  type="button"
                  onClick={(e) => { e.stopPropagation(); openPostById(featured); }}
                >
                  Weiterlesen <span aria-hidden="true">→</span>
                </button>
              </div>
            </article>
          ) : null}
        </div>
      </section>

      <section className="ein-section ein-section--tint">
        <div className="ein-container">
          <div className="ein-grid">
            {paginated.map((entry) => (
              <article className="ein-card" key={entry.id} onClick={() => openPostById(entry)}>
                <div className="ein-card__media">
                  <img src={entry.image} alt={entry.title} loading="lazy" />
                </div>
                <div className="ein-card__body">
                  <div className="ein-meta">
                    {entry.category ? <span className="ein-pill">{entry.category}</span> : null}
                    {entry.date ? <span className="ein-date">{entry.date}</span> : null}
                  </div>
                  <h3 className="ein-h3">{entry.title}</h3>
                  <p className="ein-muted ein-card__excerpt">{entry.excerpt}</p>
                  <button
                    className="ein-link ein-link--button"
                    type="button"
                    onClick={(e) => { e.stopPropagation(); openPostById(entry); }}
                  >
                    Weiterlesen <span aria-hidden="true">→</span>
                  </button>
                </div>
              </article>
            ))}
          </div>

          {totalPages > 1 ? (
            <nav className="ein-pagination" aria-label="Seitennavigation">
              <button type="button" className="ein-pageBtn" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={safePage === 1}>Zurück</button>
              <div className="ein-pageNumbers">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button key={page} type="button" className={`ein-pageNumber ${page === safePage ? "is-active" : ""}`} onClick={() => setCurrentPage(page)} aria-current={page === safePage ? "page" : undefined}>{page}</button>
                ))}
              </div>
              <button type="button" className="ein-pageBtn" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}>Weiter</button>
            </nav>
          ) : null}
        </div>
      </section>

      {openPost ? <ArticleOverlay post={openPost} onClose={closePost} /> : null}
    </Page>
  );
}

function ArticleOverlay({ post, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Solange der Artikel offen ist, darf die Seite dahinter nicht mitscrollen.
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previousOverflow; };
  }, []);

  return (
    <div className="ein-overlay" role="dialog" aria-modal="true" aria-label={post.title}>
      <button className="ein-overlay__backdrop" aria-label="Schließen" onClick={onClose} />
      <article className="ein-overlay__panel">
        <div className="ein-overlay__topbar">
          <div className="ein-meta">
            {post.category ? <span className="ein-pill">{post.category}</span> : null}
            {post.date ? <span className="ein-date">{post.date}</span> : null}
          </div>
          <button type="button" className="ein-overlay__close" onClick={onClose} aria-label="Schließen">✕</button>
        </div>

        {post.image ? (
          <div className="ein-overlay__hero">
            <img src={post.image} alt={post.title} />
          </div>
        ) : null}

        <div className="ein-overlay__content">
          <h2 className="ein-overlay__title">{post.title}</h2>
          <p className="ein-overlay__lead">{post.excerpt}</p>

          <div className="ein-overlay__body">
            <BlocksRenderer content={post.content} />
          </div>

          {post.gallery?.length > 0 ? (
            <div className="ein-overlay__gallery" aria-label="Weitere Bilder">
              {post.gallery.map((src, i) => (
                <figure className="ein-overlay__galleryItem" key={i}>
                  <img src={src} alt={post.title} loading="lazy" />
                </figure>
              ))}
            </div>
          ) : null}
        </div>
      </article>
    </div>
  );
}